import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API_CONFIG from '../utils/apiConfig';
import { useProfileShellDashboardNav } from '../hooks/useProfileShellDashboardNav';
import './SubcontractorInviteOnboarding.css';

/** Trim; empty → null so API can clear optional contact/address fields. */
function optionalContactField(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

const SubcontractorInviteOnboarding = ({ onCompleted }) => {
  const navigate = useNavigate();
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const finishOnboarding = async () => {
    window.dispatchEvent(new Event('user-updated'));
    const cb = onCompletedRef.current;
    if (typeof cb === 'function') {
      await cb();
      return;
    }
    navigate('/dashboard');
  };
  
  // Get user data from localStorage to check current step
  const [userData, setUserData] = useState(null);
  useProfileShellDashboardNav(userData);
  const [currentStep, setCurrentStep] = useState('business_type');
  const [formData, setFormData] = useState({
    businessType: '',
    tradeType: '',
    tradeTypeOther: '',
    cisLiable: '',
    // Personal registration fields (forename/surname come from account registration)
    tradingName: '',
    niNumber: '',
    utr: '',
    phone: '',
    postcode: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: '',
    addressLine5: '',
    city: '',
    // Company registration fields
    registeredName: '',
    companyType: '',
    contactName: '',
    // accountsOfficeReference - Removed: Now system-wide (stored in ENV variables)
    payeReference: '',
    // VAT registration
    vatRegistered: '',
    vatNumber: '',
    // Banking information
    accountHolderName: '',
    sortCode: '',
    accountNumber: '',
    verified: false,
    termsAccepted: false,
    privacyAccepted: false
  });
  const bankDetailsAlreadyProvided = Boolean(
    String(formData.accountHolderName || '').trim() &&
      String(formData.sortCode || '').trim() &&
      String(formData.accountNumber || '').trim()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [showCannotContinue, setShowCannotContinue] = useState(false);
  const [vatValidating, setVatValidating] = useState(false);
  const [vatValidationError, setVatValidationError] = useState('');
  const [vatValidationSuccess, setVatValidationSuccess] = useState(false);
  const [vatValidated, setVatValidated] = useState(false); // Track if VAT has been validated
  const [showTaxWarning, setShowTaxWarning] = useState(false);
  const [niValidationError, setNiValidationError] = useState('');
  const [pendingInviteRelationship, setPendingInviteRelationship] = useState(null);
  const [pendingInviteContractTerms, setPendingInviteContractTerms] = useState('');
  const [pendingInviteEmployerName, setPendingInviteEmployerName] = useState('');
  const [inviteContractAccepted, setInviteContractAccepted] = useState(false);

  const normaliseNiNumber = (value) => (value || '').toUpperCase().replace(/\s+/g, '');
  const isValidNiNumber = (value) =>
    /^[A-CEGHJ-NPR-TW-Z]{2}\d{6}[A-D]$/.test(value) && value !== 'AA000000A';

  useEffect(() => {
    // Fetch fresh user data from API to get the latest registration step
    const fetchUserData = async () => {
      try {
        const accessToken = localStorage.getItem('access_token');
        
        if (!accessToken) {
          navigate('/login');
          return;
        }

        const response = await fetch(API_CONFIG.getApiUrl(API_CONFIG.endpoints.me), {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
        if (response.ok) {
          const user = await response.json();
          setUserData(user);
          // Update localStorage with latest user data
          localStorage.setItem('user', JSON.stringify(user));
          
          // Check if user is super admin - redirect immediately (super admin doesn't need registration)
          const isSuperAdmin = user.role === 'superuser' || user.role === 'super_admin';
          if (isSuperAdmin) {
            // Dispatch event to update App.js user state
            window.dispatchEvent(new Event('user-updated'));
            // Small delay to ensure state updates, then redirect
            setTimeout(() => {
              finishOnboarding();
            }, 100);
            return;
          }
          
          // Check if user has completed registration FIRST
          const hasBusinessType = user.business_type && user.business_type !== '';
          const hasTradeType = user.trade_type && user.trade_type !== '';
          const hasFullName = user.full_name && user.full_name !== '';
          const profileFieldsComplete =
            !user.registration_step &&
            hasBusinessType &&
            hasTradeType &&
            hasFullName;
          const isRegistrationComplete = user.subcontractor_invite_onboarding_completed === true || (
            user.subcontractor_invite_onboarding_completed !== false &&
            profileFieldsComplete
          );

          // If registration is complete, redirect immediately (before setting steps)
          if (isRegistrationComplete) {
            window.dispatchEvent(new Event('user-updated'));
            setTimeout(() => {
              finishOnboarding();
            }, 100);
            return;
          }

          // Set the current step based on what the user has completed
          // Determine which step to show based on completed data
          if (user.registration_step) {
            let rs = user.registration_step === 'self_employed' ? 'cis_liable' : user.registration_step;
            if (rs === 'personal_registration' || rs === 'company_registration') {
              rs = 'invite_trading';
            }
            setCurrentStep(rs);
          } else if (!user.business_type || user.business_type === '') {
            setCurrentStep('business_type');
          } else if (!user.trade_type || user.trade_type === '') {
            setCurrentStep('trade_type');
          } else if (!user.cis_liable) {
            setCurrentStep('cis_liable');
          } else {
            setCurrentStep('invite_trading');
          }
          
          // Load any saved data
          if (user.business_type) {
            setFormData(prev => ({ ...prev, businessType: user.business_type }));
          }
          if (user.trade_type) {
            setFormData(prev => ({ ...prev, tradeType: user.trade_type }));
          }
          if (user.vat_registered) {
            setFormData(prev => ({ ...prev, vatRegistered: user.vat_registered }));
          }
          if (user.vat_number_form) {
            setFormData(prev => ({ ...prev, vatNumber: user.vat_number_form }));
          }
          if (user.terms_accepted) {
            setFormData(prev => ({ ...prev, termsAccepted: user.terms_accepted }));
          }
          if (user.privacy_accepted) {
            setFormData(prev => ({ ...prev, privacyAccepted: user.privacy_accepted }));
          }

          // Prefill existing contact/address/banking so invited existing users don't re-enter data.
          // (Only fill when the current form value is blank.)
          const addressLines = String(user.address || '')
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);
          setFormData((prev) => ({
            ...prev,
            phone: prev.phone || user.phone_number || '',
            postcode: prev.postcode || user.postal_code || '',
            city: prev.city || user.city || '',
            addressLine1: prev.addressLine1 || addressLines[0] || '',
            addressLine2: prev.addressLine2 || addressLines[1] || '',
            addressLine3: prev.addressLine3 || addressLines[2] || '',
            addressLine4: prev.addressLine4 || addressLines[3] || '',
            addressLine5: prev.addressLine5 || addressLines[4] || '',
            accountHolderName:
              prev.accountHolderName ||
              user.account_holder_name ||
              user.bank_account_name ||
              '',
            sortCode:
              prev.sortCode ||
              user.sort_code ||
              user.bank_sort_code ||
              '',
            accountNumber:
              prev.accountNumber ||
              user.account_number ||
              user.bank_account_number ||
              '',
          }));
        } else {
          // If API call fails, fall back to localStorage
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const user = JSON.parse(storedUser);
            setUserData(user);

            if (user.registration_step && user.registration_step !== 'completed') {
              let rs = user.registration_step === 'self_employed' ? 'cis_liable' : user.registration_step;
              if (rs === 'personal_registration' || rs === 'company_registration') {
                rs = 'invite_trading';
              }
              setCurrentStep(rs);
            }
          }
        }
      } catch (err) {
        // Fall back to localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          setUserData(user);

          if (user.registration_step && user.registration_step !== 'completed') {
            let rs = user.registration_step === 'self_employed' ? 'cis_liable' : user.registration_step;
            if (rs === 'personal_registration' || rs === 'company_registration') {
              rs = 'invite_trading';
            }
            setCurrentStep(rs);
          }
        }
      }
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    const fetchPendingEmployerInviteTerms = async () => {
      if (!userData) {
        setPendingInviteRelationship(null);
        setPendingInviteContractTerms('');
        setPendingInviteEmployerName('');
        return;
      }
      const regType = String(userData.registration_type || '').toLowerCase();
      const needsInviteTerms =
        userData.use_subcontractor_dashboard_shell ||
        regType === 'invite' ||
        userData.current_role === 'subcontractor';
      if (!needsInviteTerms) {
        setPendingInviteRelationship(null);
        setPendingInviteContractTerms('');
        setPendingInviteEmployerName('');
        return;
      }
      try {
        const relResp = await API_CONFIG.fetchWithAuth(
          API_CONFIG.getApiUrl('/unified-auth/relationships?status_filter=pending&page=1&per_page=25')
        );
        if (!relResp.ok) {
          setPendingInviteRelationship(null);
          return;
        }
        const relJson = await relResp.json();
        const pendingEmployerInvite = (relJson.items || []).find(
          (item) => item.status === 'pending' && item.pending_initiated_by === 'employer'
        );
        if (!pendingEmployerInvite) {
          setPendingInviteRelationship(null);
          setPendingInviteContractTerms('');
          setPendingInviteEmployerName('');
          return;
        }

        setPendingInviteRelationship(pendingEmployerInvite);
        setPendingInviteEmployerName(
          pendingEmployerInvite.employer_name || pendingEmployerInvite.employer_company_name || 'your employer'
        );

        const termsResp = await API_CONFIG.fetchWithAuth(
          API_CONFIG.getApiUrl(`/unified-auth/employer/${pendingEmployerInvite.employer_id}/contract-terms`)
        );
        if (termsResp.ok) {
          const termsJson = await termsResp.json();
          setPendingInviteContractTerms(termsJson.contract_terms || '');
          setPendingInviteEmployerName(
            termsJson.employer_company_name || termsJson.employer_name || pendingEmployerInvite.employer_name || 'your employer'
          );
        }
      } catch (err) {
        // Non-blocking: onboarding can continue even if contract terms fetch fails.
      }
    };

    fetchPendingEmployerInviteTerms();
  }, [userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For UTR field, only allow digits and limit to 10 characters
    if (name === 'utr') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }));
    } else if (name === 'niNumber') {
      const ni = normaliseNiNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: ni
      }));
      if (!ni) {
        setNiValidationError('');
      } else if (!isValidNiNumber(ni)) {
        setNiValidationError('Invalid NI Number format. Use AB123456C');
      } else {
        setNiValidationError('');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateVAT = async (vatNumber) => {
    if (!vatNumber || vatNumber.trim() === '') {
      setVatValidationError('VAT number is required');
      setVatValidated(false);
      return;
    }
    
    setVatValidating(true);
    setVatValidationError('');
    setVatValidationSuccess(false);
    setVatValidated(false);
    
    try {
      const response = await fetch(API_CONFIG.getApiUrl('/unified-auth/validate-vat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ vat_number: vatNumber })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setVatValidationSuccess(true);
          setVatValidationError('');
          setVatValidated(true);
        } else {
          setVatValidationError(data.message || 'Invalid VAT number. Please check and try again.');
          setVatValidationSuccess(false);
          setVatValidated(false);
        }
      } else {
        const errorData = await response.json();
        setVatValidationError(errorData.detail || 'Unable to validate VAT number. Please try again.');
        setVatValidationSuccess(false);
        setVatValidated(false);
      }
    } catch (err) {
      setVatValidationError('Network error. Please try again.');
      setVatValidationSuccess(false);
      setVatValidated(false);
    } finally {
      setVatValidating(false);
    }
  };

  const handleSaveAndNext = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWarning('');
    setShowTaxWarning(false);

    try {
      let response;
      
      if (currentStep === 'business_type') {
        if (!formData.businessType) {
          setError('Please select a business type');
          setLoading(false);
          return;
        }
        
        response = await fetch(API_CONFIG.getApiUrl('/unified-auth/save-registration-step'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            business_type: formData.businessType,
            registration_step: 'trade_type'
          }),
        });
      } else if (currentStep === 'trade_type') {
        if (!formData.tradeType) {
          setError('Please select a trade type');
          setLoading(false);
          return;
        }
        
        // If "Other" is selected, require the "Other" field to be filled
        if (formData.tradeType === 'Other (please specify)' && !formData.tradeTypeOther.trim()) {
          setError('Please specify your trade type');
          setLoading(false);
          return;
        }
        
        // Use the "other" value if "Other" is selected, otherwise use the selected trade type
        const tradeTypeValue = formData.tradeType === 'Other (please specify)' 
          ? formData.tradeTypeOther 
          : formData.tradeType;
        
        response = await fetch(API_CONFIG.getApiUrl('/unified-auth/save-registration-step'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            trade_type: tradeTypeValue,
            registration_step: 'cis_liable'
          }),
        });
      } else if (currentStep === 'cis_liable') {
        if (!formData.cisLiable) {
          setError('Please answer if you are registered under CIS');
          setLoading(false);
          return;
        }
        
        response = await fetch(API_CONFIG.getApiUrl('/unified-auth/save-registration-step'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            cis_liable: formData.cisLiable,
            registration_step: 'invite_trading'
          }),
        });
      } else if (currentStep === 'invite_trading') {
        const payload = { registration_step: 'vat_registration' };
        const trading = (formData.tradingName || '').trim();
        if (trading) payload.trading_name = trading;
        response = await fetch(API_CONFIG.getApiUrl('/unified-auth/save-registration-step'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(payload),
        });
      } else if (currentStep === 'invite_utr') {
        const utrDigits = (formData.utr || '').replace(/\D/g, '').slice(0, 10);
        if (utrDigits && utrDigits.length !== 10) {
          setError('UTR must be exactly 10 digits, or leave it blank.');
          setLoading(false);
          return;
        }
        const payload = { registration_step: 'vat_registration' };
        if (utrDigits.length === 10) payload.utr = utrDigits;
        response = await fetch(API_CONFIG.getApiUrl('/unified-auth/save-registration-step'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(payload),
        });
      } else if (currentStep === 'invite_bank') {
        const ah = (formData.accountHolderName || '').trim();
        const sc = (formData.sortCode || '').trim();
        const an = (formData.accountNumber || '').trim();
        if (ah || sc || an) {
          if (!ah || !sc || !an) {
            setError('Enter all bank fields, or leave all blank to skip for now.');
            setLoading(false);
            return;
          }
        }
        response = await fetch(API_CONFIG.getApiUrl('/unified-auth/save-registration-step'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            account_holder_name: ah || null,
            sort_code: sc || null,
            account_number: an || null,
            verified: false,
            registration_step: null
          }),
        });
      } else if (currentStep === 'vat_registration') {
        if (!formData.vatRegistered) {
          setError('Please select whether you are registered for VAT');
          setLoading(false);
          return;
        }
        if (formData.vatRegistered === 'Yes' && !formData.vatNumber.trim()) {
          setError('Please enter your VAT number');
          setLoading(false);
          return;
        }
        if (!formData.termsAccepted || !formData.privacyAccepted) {
          setError('Please accept the Terms and Privacy Policy to continue');
          setLoading(false);
          return;
        }
        if (pendingInviteRelationship && !inviteContractAccepted) {
          setError('Please accept the employer contract terms to continue');
          setLoading(false);
          return;
        }

        response = await fetch(API_CONFIG.getApiUrl('/unified-auth/save-registration-step'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            vat_registered: formData.vatRegistered,
            vat_number_form: formData.vatRegistered === 'Yes' ? formData.vatNumber.trim() : null,
            terms_accepted: formData.termsAccepted,
            privacy_accepted: formData.privacyAccepted,
            registration_step: 'invite_bank'
          }),
        });
      }

      if (response && response.ok) {
        const updatedUser = await response.json();
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Move to next step or complete
        if (currentStep === 'business_type') {
          setCurrentStep('trade_type');
        } else if (currentStep === 'trade_type') {
          setCurrentStep('cis_liable');
        } else if (currentStep === 'cis_liable') {
          setCurrentStep('invite_trading');
        } else if (currentStep === 'invite_trading') {
          setCurrentStep('invite_utr');
        } else if (currentStep === 'invite_utr') {
          setCurrentStep('vat_registration');
        } else if (currentStep === 'vat_registration') {
          setCurrentStep('invite_bank');
        } else if (currentStep === 'invite_bank') {
          // Registration complete - refresh user data and redirect to dashboard
          try {
            // Fetch updated user data from API
            const userResponse = await fetch(API_CONFIG.getApiUrl('/unified-auth/me'), {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            
            if (userResponse.ok) {
              const userData = await userResponse.json();
              localStorage.setItem('user', JSON.stringify(userData));
              
              // Dispatch event to notify App.js to update user state
              window.dispatchEvent(new Event('user-updated'));
              
              // Small delay to ensure state update
              setTimeout(() => {
                finishOnboarding();
              }, 100);
            } else {
              finishOnboarding();
            }
          } catch (err) {
            finishOnboarding();
          }
        }
      } else if (response) {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save information');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = () => {
    setShowCannotContinue(false);
    setCurrentStep('business_type');
    setInviteContractAccepted(false);
    setFormData({
      businessType: '',
      tradeType: '',
      tradeTypeOther: '',
      cisLiable: '',
      tradingName: '',
      niNumber: '',
      utr: '',
      phone: '',
      postcode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: '',
      addressLine5: '',
      city: '',
      registeredName: '',
      companyType: '',
      contactName: '',
      // accountsOfficeReference - Removed: Now system-wide (stored in ENV variables)
      payeReference: '',
      vatRegistered: '',
      vatNumber: '',
      // Banking information
      accountHolderName: '',
      sortCode: '',
      accountNumber: '',
      verified: false,
      termsAccepted: false,
      privacyAccepted: false
    });
  };

  const getPreviousStep = (step) => {
    if (step === 'trade_type') return 'business_type';
    if (step === 'cis_liable') return 'trade_type';
    if (step === 'invite_trading') return 'cis_liable';
    if (step === 'invite_utr') return 'invite_trading';
    if (step === 'vat_registration') return 'invite_utr';
    if (step === 'invite_bank') return 'vat_registration';
    return null;
  };

  const handleBack = () => {
    setError('');
    setWarning('');
    setShowTaxWarning(false);
    const prev = getPreviousStep(currentStep);
    if (prev) setCurrentStep(prev);
  };

  
  if (showCannotContinue) {
    return (
      <div className="profile-completion-container">
        <div className="profile-completion-form">
          <div className="form-header">
            <h2>Cannot Continue Registration</h2>
            <p>Sorry, you cannot continue with registration based on your answers.</p>
          </div>
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <button onClick={handleStartOver} className="complete-profile-btn">
              Start from Beginning
            </button>
            <button onClick={() => navigate('/')} className="complete-profile-btn" style={{ marginTop: '15px', background: '#6c757d' }}>
              Leave
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-completion-container">
      <div className="profile-completion-form">
        <div className="form-header">
          <h2>Complete Your Registration</h2>
          {/* <p>Please provide the following information to continue (Current Step: {currentStep})</p> */}
        </div>

        {warning && showTaxWarning && (
          <div style={{
            background: '#fff3cd',
            color: '#856404',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #ffc107',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ flexShrink: 0, marginTop: '2px' }}
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <div>
              <strong>Warning:</strong> {warning}
            </div>
          </div>
        )}

        <form onSubmit={handleSaveAndNext}>
          {/* Step 1: Business Type Selection */}
          {currentStep === 'business_type' && (
            <div className="form-group">
              <label htmlFor="businessType">Select Business Type *</label>
              <select
                id="businessType"
                name="businessType"
                value={formData.businessType}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Please select...</option>
                <option value="Personal (Sole Trader)">Personal (Sole Trader)</option>
                <option value="Business (Ltd co or Partnership)">Business (Ltd co or Partnership)</option>
              </select>
            </div>
          )}

          {/* Step 2: Trade Type Selection */}
          {currentStep === 'trade_type' && (
            <div className="form-group">
              <label htmlFor="tradeType">Select Trade Type *</label>
              <select
                id="tradeType"
                name="tradeType"
                value={formData.tradeType}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Please select...</option>
                <option value="Architect / Designer / Surveyor (consultancy only)">Architect / Designer / Surveyor (consultancy only)</option>
                <option value="Asbestos remover">Asbestos remover</option>
                <option value="Bathroom fitter">Bathroom fitter</option>
                <option value="Brick / Block layer">Brick / Block layer</option>
                <option value="Canteen operator / Welfare provider">Canteen operator / Welfare provider</option>
                <option value="Carpenter / Joiner">Carpenter / Joiner</option>
                <option value="Civil engineer (on-site works)">Civil engineer (on-site works)</option>
                <option value="Cladder / Curtain wall installer">Cladder / Curtain wall installer</option>
                <option value="Cleaner (construction / builders' clean)">Cleaner (construction / builders' clean)</option>
                <option value="Cleaner (domestic / maintenance)">Cleaner (domestic / maintenance)</option>
                <option value="Concrete worker / Paver / Kerber">Concrete worker / Paver / Kerber</option>
                <option value="Crane hire (without labour)">Crane hire (without labour)</option>
                <option value="Crane operator (with labour)">Crane operator (with labour)</option>
                <option value="Delivery driver / Haulage">Delivery driver / Haulage</option>
                <option value="Demolition worker">Demolition worker</option>
                <option value="Drainage / Pipework installer (external)">Drainage / Pipework installer (external)</option>
                <option value="Electrician">Electrician</option>
                <option value="Engineer (consulting only)">Engineer (consulting only)</option>
                <option value="Fire alarm / Security system installer">Fire alarm / Security system installer</option>
                <option value="Floor fitter / Carpet layer">Floor fitter / Carpet layer</option>
                <option value="General contractor / Builder">General contractor / Builder</option>
                <option value="Ground investigation / Borehole driller">Ground investigation / Borehole driller</option>
                <option value="Groundworker / Excavator / Digger driver">Groundworker / Excavator / Digger driver</option>
                <option value="HVAC installer / Ventilation engineer">HVAC installer / Ventilation engineer</option>
                <option value="Insulation installer">Insulation installer</option>
                <option value="Kitchen fitter">Kitchen fitter</option>
                <option value="Landscaper (hard landscaping)">Landscaper (hard landscaping)</option>
                <option value="Landscaper (soft landscaping only)">Landscaper (soft landscaping only)</option>
                <option value="Leadworker">Leadworker</option>
                <option value="Lift / Elevator installer">Lift / Elevator installer</option>
                <option value="Painter / Decorator">Painter / Decorator</option>
                <option value="Pest control / Environmental cleaning">Pest control / Environmental cleaning</option>
                <option value="Plant hire (without operator)">Plant hire (without operator)</option>
                <option value="Plant operator (with labour)">Plant operator (with labour)</option>
                <option value="Plasterer / Plasterboard fixer / Dry liner / Taper & jointer">Plasterer / Plasterboard fixer / Dry liner / Taper & jointer</option>
                <option value="Plumber / Heating engineer">Plumber / Heating engineer</option>
                <option value="Roofer">Roofer</option>
                <option value="Scaffold hire (no labour)">Scaffold hire (no labour)</option>
                <option value="Scaffolder (with labour)">Scaffolder (with labour)</option>
                <option value="Sign fitter / Fascia installer">Sign fitter / Fascia installer</option>
                <option value="Site Manager / Foreman (self-employed)">Site Manager / Foreman (self-employed)</option>
                <option value="Site security / Watchman">Site security / Watchman</option>
                <option value="Steel fixer / Fabricator / Welder (on site)">Steel fixer / Fabricator / Welder (on site)</option>
                <option value="Supplier of materials only">Supplier of materials only</option>
                <option value="Telecoms / Data installer">Telecoms / Data installer</option>
                <option value="Temporary works / Site hoarding erector">Temporary works / Site hoarding erector</option>
                <option value="Tiler (wall / floor / roof)">Tiler (wall / floor / roof)</option>
                <option value="Tree surgeon / Arborist">Tree surgeon / Arborist</option>
                <option value="Waste removal (with labour / site clearance)">Waste removal (with labour / site clearance)</option>
                <option value="Waste removal / Skip hire (no labour)">Waste removal / Skip hire (no labour)</option>
                <option value="Window fitter / Glazier">Window fitter / Glazier</option>
                <option value="Other (please specify)">Other (please specify)</option>
              </select>
              
              {formData.tradeType === 'Other (please specify)' && (
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label htmlFor="tradeTypeOther">Please specify your trade type *</label>
                  <input
                    type="text"
                    id="tradeTypeOther"
                    name="tradeTypeOther"
                    value={formData.tradeTypeOther}
                    onChange={handleInputChange}
                    required={formData.tradeType === 'Other (please specify)'}
                    placeholder="Enter your trade type"
                  />
                </div>
              )}
            </div>
          )}

          {/* CIS (Construction Industry Scheme) */}
          {currentStep === 'cis_liable' && (
            <div className="form-group">
              <label htmlFor="cisLiable">Are you registered under the Construction Industry Scheme (CIS)?</label>
              <select
                id="cisLiable"
                name="cisLiable"
                value={formData.cisLiable}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Please select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {formData.cisLiable === 'No' && (
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666', 
                  marginTop: '10px',
                  padding: '12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '4px'
                }}>
                  <p style={{ margin: '0 0 8px 0' }}>
                    If you're self-employed, a limited company, or a partnership and you carry out construction work, you must register for the Construction Industry Scheme (CIS).
                  </p>
                  <p style={{ margin: '0 0 8px 0' }}>
                    If you haven't registered yet, your payments maybe taxed at 30% until you do.
                  </p>
                  <p style={{ margin: '0 0 8px 0' }}>
                    For employers, you must register under the CIS scheme in order to verify and pay your subcontractors, if you are obligated to do so by HMRC.
                  </p>
                  <p style={{ margin: 0 }}>
                    <a href="https://www.gov.uk/register-cis" target="_blank" rel="noopener noreferrer">
                      You can register online at www.gov.uk/register-cis
                    </a>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Personal registration (sole trader) */}
          {currentStep === 'personal_registration' && (
            <div>
              <h3 style={{ marginBottom: '20px', color: '#2a4252' }}>Personal Registration</h3>
              <div className="form-group">
                <label htmlFor="tradingName">Trading Name</label>
                <input type="text" id="tradingName" name="tradingName" value={formData.tradingName} onChange={handleInputChange} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="niNumber">NI Number</label>
                  <input
                    type="text"
                    id="niNumber"
                    name="niNumber"
                    value={formData.niNumber}
                    onChange={handleInputChange}
                    placeholder="AB123456C"
                    maxLength="9"
                  />
                  {niValidationError && (
                    <small style={{ color: '#dc3545', display: 'block', marginTop: '6px' }}>
                      {niValidationError}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="utr" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    UTR
                    <span className="tooltip-container">
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{ color: '#57bedd' }}
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                      </svg>
                      <span className="tooltip-text">
                        You can find your ten digit UTR on tax documents or any correspondence from HMRC.
                      </span>
                    </span>
                  </label>
                  <input 
                    type="text" 
                    id="utr" 
                    name="utr" 
                    value={formData.utr} 
                    onChange={handleInputChange} 
                    maxLength="10"
                    inputMode="numeric"
                    placeholder="Enter 10 digit UTR (optional)"
                    style={{
                      fontFamily: 'monospace',
                      letterSpacing: '2px'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="phone">Phone (optional)</label>
                  <input type="text" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} autoComplete="tel" />
                </div>
                <div className="form-group">
                  <label htmlFor="postcode">Postcode (optional)</label>
                  <input type="text" id="postcode" name="postcode" value={formData.postcode} onChange={handleInputChange} autoComplete="postal-code" />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="addressLine1">Address Line 1 (optional)</label>
                <input type="text" id="addressLine1" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} autoComplete="address-line1" />
              </div>
              <div className="form-group">
                <label htmlFor="addressLine2">Address Line 2</label>
                <input type="text" id="addressLine2" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="addressLine3">Address Line 3</label>
                  <input type="text" id="addressLine3" name="addressLine3" value={formData.addressLine3} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="addressLine4">Address Line 4</label>
                  <input type="text" id="addressLine4" name="addressLine4" value={formData.addressLine4} onChange={handleInputChange} />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="addressLine5">Address Line 5</label>
                <input type="text" id="addressLine5" name="addressLine5" value={formData.addressLine5} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label htmlFor="city">City (optional)</label>
                <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} autoComplete="address-level2" />
              </div>

              {/* Banking Details Section */}
              {!bankDetailsAlreadyProvided ? (
                <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                  <h4 style={{ marginBottom: '15px', color: '#2a4252' }}>Banking Details</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label htmlFor="accountHolderName">Account Holder Name</label>
                      <input 
                        type="text" 
                        id="accountHolderName" 
                        name="accountHolderName" 
                        value={formData.accountHolderName} 
                        onChange={handleInputChange} 
                        placeholder="Name on account"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label htmlFor="sortCode">Sort Code</label>
                      <input 
                        type="text" 
                        id="sortCode" 
                        name="sortCode" 
                        value={formData.sortCode} 
                        onChange={handleInputChange} 
                        maxLength="6"
                        placeholder="123456"
                        style={{
                          fontFamily: 'monospace',
                          letterSpacing: '1px'
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="accountNumber">Account Number</label>
                      <input 
                        type="text" 
                        id="accountNumber" 
                        name="accountNumber" 
                        value={formData.accountNumber} 
                        onChange={handleInputChange} 
                        maxLength="8"
                        placeholder="12345678"
                        style={{
                          fontFamily: 'monospace',
                          letterSpacing: '1px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                  <h4 style={{ marginBottom: '8px', color: '#2a4252' }}>Banking Details</h4>
                  <div style={{ color: '#2a4252', fontSize: '14px' }}>
                    Bank details are already saved for your organisation, so you don’t need to enter them again.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 6: Company Registration Fields (if CIS = Yes) */}
          {currentStep === 'company_registration' && (
            <div>
              <h3 style={{ marginBottom: '20px' }}>Company Registration</h3>
              <div className="form-group">
                <label htmlFor="registeredName">Registered Name *</label>
                <input type="text" id="registeredName" name="registeredName" value={formData.registeredName} onChange={handleInputChange} required />
              </div>
              {String(formData.companyType || '').trim().toUpperCase() !== 'LTD' && (
                <div className="form-group">
                  <label htmlFor="tradingName">Trading Name</label>
                  <input type="text" id="tradingName" name="tradingName" value={formData.tradingName} onChange={handleInputChange} />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="utr" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  UTR
                  <span className="tooltip-container">
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ color: '#57bedd' }}
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    <span className="tooltip-text">
                      You can find your ten digit UTR on tax documents or any correspondence from HMRC.
                    </span>
                  </span>
                </label>
                <input 
                  type="text" 
                  id="utr" 
                  name="utr" 
                  value={formData.utr} 
                  onChange={handleInputChange} 
                  maxLength="10"
                  inputMode="numeric"
                  placeholder="Enter 10 digit UTR (optional)"
                  style={{
                    fontFamily: 'monospace',
                    letterSpacing: '2px'
                  }}
                />
              </div>
              <div className="form-group">
                <label htmlFor="companyType">Company Type *</label>
                <select id="companyType" name="companyType" value={formData.companyType} onChange={handleInputChange} required>
                  <option value="">Please select...</option>
                  <option value="LTD">LTD</option>
                  <option value="Partnership">Partnership</option>
                  <option value="LLP">LLP</option>
                </select>
              </div>
              {/* Accounts Office Reference and PAYE Reference - Removed: Now stored in ENV variables (system-wide) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="contactName">Contact Name *</label>
                  <input type="text" id="contactName" name="contactName" value={formData.contactName} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone (optional)</label>
                  <input type="text" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} autoComplete="tel" />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="postcode">Postcode (optional)</label>
                <input type="text" id="postcode" name="postcode" value={formData.postcode} onChange={handleInputChange} autoComplete="postal-code" />
              </div>
              <div className="form-group">
                <label htmlFor="addressLine1">Address Line 1 (optional)</label>
                <input type="text" id="addressLine1" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} autoComplete="address-line1" />
              </div>
              <div className="form-group">
                <label htmlFor="addressLine2">Address Line 2</label>
                <input type="text" id="addressLine2" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="addressLine3">Address Line 3</label>
                  <input type="text" id="addressLine3" name="addressLine3" value={formData.addressLine3} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="addressLine4">Address Line 4</label>
                  <input type="text" id="addressLine4" name="addressLine4" value={formData.addressLine4} onChange={handleInputChange} />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="addressLine5">Address Line 5</label>
                <input type="text" id="addressLine5" name="addressLine5" value={formData.addressLine5} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label htmlFor="city">City (optional)</label>
                <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} autoComplete="address-level2" />
              </div>

              {/* Banking Details Section */}
              {!bankDetailsAlreadyProvided ? (
                <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                  <h4 style={{ marginBottom: '15px' }}>Banking Details</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label htmlFor="accountHolderName">Account Holder Name</label>
                      <input 
                        type="text" 
                        id="accountHolderName" 
                        name="accountHolderName" 
                        value={formData.accountHolderName} 
                        onChange={handleInputChange} 
                        placeholder="Name on account"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label htmlFor="sortCode">Sort Code</label>
                      <input 
                        type="text" 
                        id="sortCode" 
                        name="sortCode" 
                        value={formData.sortCode} 
                        onChange={handleInputChange} 
                        maxLength="6"
                        placeholder="123456"
                        style={{
                          fontFamily: 'monospace',
                          letterSpacing: '1px'
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="accountNumber">Account Number</label>
                      <input 
                        type="text" 
                        id="accountNumber" 
                        name="accountNumber" 
                        value={formData.accountNumber} 
                        onChange={handleInputChange} 
                        maxLength="8"
                        placeholder="12345678"
                        style={{
                          fontFamily: 'monospace',
                          letterSpacing: '1px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                  <h4 style={{ marginBottom: '8px' }}>Banking Details</h4>
                  <div style={{ color: '#2a4252', fontSize: '14px' }}>
                    Bank details are already saved for your organisation, so you don’t need to enter them again.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 7: VAT Registration (final step) */}
          {currentStep === 'vat_registration' && (
            <div>
              <div className="form-group">
                <label htmlFor="vatRegistered">Are you registered for VAT? *</label>
                <select
                  id="vatRegistered"
                  name="vatRegistered"
                  value={formData.vatRegistered}
                  onChange={handleInputChange}
                  required
                  className="form-select"
                >
                  <option value="">Please select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              
              {formData.vatRegistered === 'Yes' && (
                <div className="form-group">
                  <label htmlFor="vatNumber">VAT Number *</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <input
                      type="text"
                      id="vatNumber"
                      name="vatNumber"
                      value={formData.vatNumber}
                      onChange={(e) => {
                        handleInputChange(e);
                        // Clear validation when user types
                        setVatValidationError('');
                        setVatValidationSuccess(false);
                        setVatValidated(false);
                      }}
                      required={formData.vatRegistered === 'Yes'}
                      placeholder="Enter your VAT number (9 digits)"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => validateVAT(formData.vatNumber)}
                      disabled={vatValidating || !formData.vatNumber || formData.vatNumber.trim() === ''}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: vatValidated ? '#28a745' : '#57bedd',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: (vatValidating || !formData.vatNumber || formData.vatNumber.trim() === '') ? 'not-allowed' : 'pointer',
                        opacity: (vatValidating || !formData.vatNumber || formData.vatNumber.trim() === '') ? 0.6 : 1,
                        whiteSpace: 'nowrap',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {vatValidating ? 'Checking...' : vatValidated ? '✓ Valid' : 'Check'}
                    </button>
                  </div>
                  {vatValidating && (
                    <div style={{ fontSize: '12px', color: '#57bedd', marginTop: '4px' }}>
                      Validating VAT number...
                    </div>
                  )}
                  {vatValidationSuccess && !vatValidating && (
                    <div style={{ fontSize: '12px', color: '#28a745', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>✓</span> VAT number validated successfully
                    </div>
                  )}
                  {vatValidationError && !vatValidating && (
                    <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>
                      {vatValidationError}
                    </div>
                  )}
                </div>
              )}
              
              <div className="terms-checkbox-wrapper" style={{ marginTop: '20px', marginBottom: '10px' }}>
                <input
                  type="checkbox"
                  id="termsAccepted"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={(e) => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                  required
                />
                <label htmlFor="termsAccepted" style={{ color: '#2a4252' }}>
                  I accept the{' '}
                  <a 
                    href="/terms-and-conditions" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#57bedd', textDecoration: 'underline' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms and Conditions
                  </a>
                  {' '}*
                </label>
              </div>
              
              <div className="terms-checkbox-wrapper" style={{ marginBottom: '20px' }}>
                <input
                  type="checkbox"
                  id="privacyAccepted"
                  name="privacyAccepted"
                  checked={formData.privacyAccepted}
                  onChange={(e) => setFormData(prev => ({ ...prev, privacyAccepted: e.target.checked }))}
                  required
                />
                <label htmlFor="privacyAccepted" style={{ color: '#2a4252' }}>
                  I accept the{' '}
                  <a 
                    href="/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#57bedd', textDecoration: 'underline' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                  {' '}*
                </label>
              </div>

              {pendingInviteRelationship && (
                <div style={{ marginBottom: '20px', padding: '14px', border: '1px solid #d9edf7', borderRadius: '8px', background: '#f4fbff' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '8px', color: '#2a4252' }}>
                    Employer Contract Terms ({pendingInviteEmployerName || 'Employer'})
                  </h4>
                  <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#495057' }}>
                    You have a pending employer invitation. Please review and accept these terms to complete onboarding.
                  </p>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '13px', color: '#2f3b46', background: '#fff', border: '1px solid #e4ecf2', borderRadius: '6px', padding: '10px' }}>
                    {pendingInviteContractTerms || 'No contract terms on file for this employer.'}
                  </div>
                  <div className="terms-checkbox-wrapper" style={{ marginTop: '10px', marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      id="inviteContractAccepted"
                      name="inviteContractAccepted"
                      checked={inviteContractAccepted}
                      onChange={(e) => setInviteContractAccepted(e.target.checked)}
                      required
                    />
                    <label htmlFor="inviteContractAccepted" style={{ color: '#2a4252' }}>
                      I have read and accept this employer contract *
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep === 'invite_trading' && (
            <div className="form-step">
              <div className="form-group">
                <label htmlFor="tradingNameNew">Do you have a trading name?</label>
                <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>Leave blank if you don’t.</p>
                <input
                  type="text"
                  id="tradingNameNew"
                  name="tradingName"
                  value={formData.tradingName}
                  onChange={handleInputChange}
                  placeholder="Enter trading name"
                />
              </div>
            </div>
          )}

          {currentStep === 'invite_utr' && (
            <div className="form-step">
              <div className="form-group">
                <label htmlFor="utrMini">Enter your unique tax reference number.</label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}><strong>UTR (optional)</strong></p>
                <input
                  type="text"
                  id="utrMini"
                  name="utr"
                  value={formData.utr}
                  onChange={handleInputChange}
                  placeholder="10 digits"
                  maxLength={10}
                  style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
                />
                <p style={{ fontSize: '13px', color: '#666', marginTop: '10px' }}>
                  You can find your UTR on any letters or documents from HMRC. You can leave this blank, but without it you may be taxed at a higher rate.
                </p>
              </div>
            </div>
          )}

          {currentStep === 'invite_bank' && (
            <div className="form-step">
              <h3 style={{ color: '#2a4252', marginTop: 0 }}>Bank Account Details</h3>
              <p style={{ fontSize: '14px', color: '#666' }}>Please enter your bank account details below</p>
              <div className="form-group">
                <label htmlFor="accountHolderNameMini">Account Holder Name</label>
                <input
                  type="text"
                  id="accountHolderNameMini"
                  name="accountHolderName"
                  value={formData.accountHolderName}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="sortCodeMini">Sort Code</label>
                  <input
                    type="text"
                    id="sortCodeMini"
                    name="sortCode"
                    value={formData.sortCode}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      sortCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                    }))}
                    maxLength={6}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="accountNumberMini">Account Number</label>
                  <input
                    type="text"
                    id="accountNumberMini"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      accountNumber: e.target.value.replace(/\D/g, '').slice(0, 8)
                    }))}
                    maxLength={8}
                  />
                </div>
              </div>
              <p style={{ fontSize: '13px', color: '#666', marginTop: '10px' }}>
                You can leave these blank for now, but you will need to provide your bank details before you can get paid.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {currentStep !== 'business_type' && (
              <button
                type="button"
                className="complete-profile-btn"
                style={{ width: 'auto', minWidth: '90px', padding: '10px 16px', background: '#6c757d' }}
                onClick={handleBack}
                disabled={loading}
              >
                Back
              </button>
            )}
            <button 
              type="submit" 
              className="complete-profile-btn"
              style={{ width: 'auto', minWidth: '120px', padding: '10px 18px' }}
              disabled={
                loading || 
                (currentStep === 'vat_registration' && formData.vatRegistered === 'Yes' && formData.vatNumber && !vatValidated) ||
                (currentStep === 'vat_registration' && (!formData.termsAccepted || !formData.privacyAccepted)) ||
                (currentStep === 'vat_registration' && !!pendingInviteRelationship && !inviteContractAccepted)
              }
            >
              {loading ? 'Saving...' : currentStep === 'invite_bank' ? 'Done' : 'Next'}
            </button>
          </div>

          {error ? (
            <div className="error-message error-message--below-submit" role="alert">
              <p style={{ margin: 0 }}>{error}</p>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
};

export default SubcontractorInviteOnboarding;
